import { useCallback, useMemo, useRef } from 'react'
import { useReselector } from '@/utils/sharedHooks'
import { isEmailVerified } from '@/selectors/user.selectors'
import { useSearchParams } from 'react-router-dom'

export const defaultMessagesURL = `https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/hc/en-us/requests?status=answered`
export const RETURN_TO_KEY = 'return_to'

export const useZendeskSso = ({
  userHasEmailVerified = false,
}: {
  userHasEmailVerified?: boolean
} = {}) => {
  const [searchParams] = useSearchParams()
  const userEmailVerified =
    useReselector(isEmailVerified) || userHasEmailVerified

  const returnToParam = searchParams.get(RETURN_TO_KEY)

  const formRef = useRef<HTMLFormElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const zendeskForm = useMemo(() => {
    const returnTo =
      localStorage.getItem(`original_${RETURN_TO_KEY}`) || returnToParam
    const actionUrl = returnTo
      ? `https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/access/jwt?${RETURN_TO_KEY}=${encodeURIComponent(
          userEmailVerified
            ? returnTo
            : `${process.env.VITE_LOCAL_API_ENDPOINT_V2}accounts/zendesk/sso`
        )}`
      : `https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/access/jwt`

    return (
      <form ref={formRef} action={actionUrl} method="post">
        <input ref={inputRef} type="hidden" name="jwt" />
      </form>
    )
  }, [returnToParam, userEmailVerified])

  const submitForm = useCallback((value: string) => {
    // Shouldn't happen if the form is initialized
    if (!inputRef.current) {
      return
    }

    // This must be done via secondary form https://support.zendesk.com/hc/en-us/articles/4408845838874-Enabling-JWT-single-sign-on#ariaid-title13
    inputRef.current.value = value

    // Navigates user to zendesk sso.  On success will navigate user back to Heard if returnToParam set.
    // User redux will be populated via session call
    formRef.current?.submit()
  }, [])

  return {
    zendeskForm,
    submitForm,
  }
}
